<script lang="ts" setup>
/**
 * リプライ投稿 > リプライ投稿メディア。
 */

import { reactive, inject, defineProps, defineEmits, watch } from "vue";
import { ReplyPostStore, isValidReplyPostStoreKey } from "@/store/communityTalk/replyPostStore";
import PostMedia from "../PostMedia.vue";
import { PostMediaViewModel } from "@/viewModels/communityTalk/postMediaViewModel";
import { isValidCommunityTalkStoreKey } from "@/store/communityTalk/communityTalkStore";

const props = defineProps({
	/**
	 * CommunityTalkStoreのInject用キー。
	 */
	communityTalkStoreKey: {
		type: String,
		required: true,
	},
	/**
	 * ReplyPostStoreのInject用キー。
	 */
	replyPostStoreKey: {
		type: String,
		required: true,
	},
	/**
	 * 入力欄を無効化するかどうか。
	 */
	disabled: {
		type: Boolean,
		required: false,
		default: false,
	},
});

// Storeの初期化処理
if (!isValidCommunityTalkStoreKey(props.communityTalkStoreKey) || !isValidReplyPostStoreKey(props.replyPostStoreKey)) {
	// なんらかの原因でInjectKeyにエラーが発生していた場合は強制終了
	throw new Error(`InjectKeyにエラーが発生しました。${props.communityTalkStoreKey.toString()}, ${props.replyPostStoreKey.toString()}`);
}
const replyPostStore = inject(props.replyPostStoreKey) as ReplyPostStore;

interface State {
	/**
	 * リプライ投稿: メディア。
	 */
	postMediaViewModels: PostMediaViewModel[];
}

// reactiveな変数宣言
const state = reactive<State>({
	postMediaViewModels: replyPostStore.getPostMediaViewModels(),
});

// Emits
interface Emits {
	// 画像・動画の投稿が開始されたイベント
	(e: "on-start-post-process"): void;
	// 画像・動画の投稿が完了したイベント
	(e: "on-end-post-process"): void;
}
const emits = defineEmits<Emits>();

// メディア投稿完了時の処理
const onSuccessUploadMedias = (viewModels: PostMediaViewModel[]): void => {
	replyPostStore.setPostMediaViewModels(viewModels);
};

// メディアリセット時の処理
const onResetMedias = (): void => {
	replyPostStore.setPostMediaViewModels([]);
};

/**
 * 投稿内容が他コンポーネント等から変更された場合、変更を反映する。
 */
watch(
	() => replyPostStore.getPostMediaViewModels(),
	(postMediaViewModels) => {
		state.postMediaViewModels = postMediaViewModels;
	}
);

// 画像・動画投稿開始イベント検知処理
const onStartPostProcess = (): void => {
	emits("on-start-post-process");
};
// 画像・動画投稿終了イベント検知処理
const onEndPostProcess = (): void => {
	emits("on-end-post-process");
};
</script>

<template>
	<post-media
		:community-talk-store-key="communityTalkStoreKey"
		:disabled="disabled"
		post-type="Reply"
		:init-post-media-view-models="replyPostStore.getPostMediaViewModels()"
		@on-success-upload-medias="onSuccessUploadMedias"
		@on-reset-medias="onResetMedias"
		@on-start-post-process="onStartPostProcess"
		@on-end-post-process="onEndPostProcess"
	/>
</template>

<style lang="scss"></style>
